import { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Switch,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  APP_USAGE_DEFAULT_WEIGHTS,
  APP_USAGE_METRIC_KEYS,
  computeAppUsage,
} from "@congrs/schemas";

import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { useGetAllFeatureFlags } from "../api/featureFlags";
import {
  useGetAppUsage,
  useGetAppUsageWeights,
  useUpdateAppUsageWeights,
} from "../api/insights";
import CategoryCard from "../components/Insights/AppUsageWeights/CategoryCard";
import ScoreHeader from "../components/Insights/AppUsageWeights/ScoreHeader";
import { rescaleAfterEdit } from "../components/Insights/AppUsageWeights/rescale";
import { roundOneDecimal } from "../components/Insights/AppUsageWeights/utils";

function AppUsageWeights() {
  const { user } = useContext(AuthContext);
  const eventId = getCurrentWorkspace(user)?.eventId;

  const [autoBalance, setAutoBalance] = useState(true);

  const { data: weightsData, isPending: weightsPending } =
    useGetAppUsageWeights(eventId);
  const { data: usageData, isPending: usagePending } = useGetAppUsage(eventId);
  const { data: flags } = useGetAllFeatureFlags({ eventId });
  const { mutate: saveWeights, isPending: isSaving } =
    useUpdateAppUsageWeights(eventId);

  const registry = weightsData?.registry;
  const defaults = weightsData?.defaults ?? APP_USAGE_DEFAULT_WEIGHTS;
  const initialWeights = weightsData?.overrides ?? weightsData?.defaults;

  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (initialWeights && !draft) setDraft(initialWeights);
  }, [initialWeights, draft]);

  const enabledFlags = useMemo(
    () =>
      (Array.isArray(flags) ? flags : [])
        .filter((f) => f.enabled)
        .map((f) => f.featureName),
    [flags]
  );

  const metricInputs = useMemo(() => {
    const out = {};
    for (const c of usageData?.categories ?? []) {
      for (const m of c.metrics ?? []) {
        out[m.key] = { numerator: m.numerator, denominator: m.denominator };
      }
    }
    return out;
  }, [usageData]);

  const preview = useMemo(() => {
    if (!draft || !usageData) return null;
    return computeAppUsage({
      weights: draft,
      metricInputs,
      activeAppUsers: usageData.activeAppUsers,
      enabledFeatureFlags: enabledFlags,
    });
  }, [draft, usageData, metricInputs, enabledFlags]);

  const total = useMemo(
    () =>
      roundOneDecimal(
        APP_USAGE_METRIC_KEYS.reduce((a, k) => a + (draft?.[k] ?? 0), 0)
      ),
    [draft]
  );

  const dirty = useMemo(() => {
    if (!draft || !initialWeights) return false;
    return APP_USAGE_METRIC_KEYS.some(
      (k) =>
        roundOneDecimal(draft[k] ?? 0) !==
        roundOneDecimal(initialWeights[k] ?? 0)
    );
  }, [draft, initialWeights]);

  const handleChangeMetric = (key, value) => {
    setDraft((current) =>
      autoBalance
        ? rescaleAfterEdit(current, key, value)
        : {
            ...current,
            [key]: Math.max(0, Math.min(100, roundOneDecimal(value))),
          }
    );
  };
  const handleReset = () => setDraft({ ...defaults });
  const handleSave = () => draft && saveWeights(draft);

  if (weightsPending || usagePending || !draft || !registry) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  const currentScore = usageData?.score ?? 0;
  const previewScore = preview?.score ?? currentScore;
  const totalOk = Math.abs(total - 100) <= 0.1;

  const includedByKey = new Map(
    (preview?.categories ?? []).map((c) => [c.key, c.included])
  );
  const orderedRegistry = [...registry].sort(
    (a, b) =>
      (includedByKey.get(a.key) ? 0 : 1) - (includedByKey.get(b.key) ? 0 : 1)
  );

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between" wrap="nowrap" align="center">
        <Group gap="xs" wrap="nowrap">
          <Button variant="default" onClick={handleReset} disabled={isSaving}>
            Reset to defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={!dirty || !totalOk || isSaving}
            loading={isSaving}
          >
            Save changes
          </Button>
        </Group>
        <Tooltip
          label={
            autoBalance
              ? "On: moving a slider rebalances the rest so the total stays 100%."
              : "Off: each slider only changes itself. Adjust everything manually."
          }
          withArrow
          w={260}
          multiline
        >
          <Switch
            checked={autoBalance}
            onChange={(e) => setAutoBalance(e.currentTarget.checked)}
            label="Auto-balance"
            size="md"
          />
        </Tooltip>
      </Group>

      <ScoreHeader
        current={currentScore}
        preview={previewScore}
        totalOk={totalOk}
        total={total}
      />

      {!totalOk && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} py={4}>
          Weights must sum to 100% before saving.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="sm">
        {orderedRegistry.map((c) => (
          <CategoryCard
            key={c.key}
            category={c}
            weights={draft}
            computed={preview?.categories.find((pc) => pc.key === c.key)}
            onChangeMetric={handleChangeMetric}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
}

export default AppUsageWeights;
